import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  isBoolean, map, noop, size,
} from 'lodash';
import Thead from '../../../core/components/table-components/Thead';
import Tr from '../../../core/components/table-components/Tr';
import ThWithControls from '../../../core/components/table-components/ThWithControls';
import Tbody from '../../../core/components/table-components/Tbody';
import TableNew from '../../../core/components/table-components/table-new/TableNew';
import {
  getTableFilter, isDefaultFilter, isFilterKey, TableFilter,
} from '../utils';
import { StockBulkTableRow } from './StockBulkTableRow';
import Checkbox from '../../../core/components/checkbox/Checkbox';
import TdNew from '../../../core/components/table-components/table-new/TdNew';
import ThNew from '../../../core/components/table-components/table-new/ThNew';
import { BulkTableProps } from '../useBulkTable';
import EmptyTableSection from '../../../core/components/table-components/EmptyTableSection';
import { PartRequestBulkItemDto } from '../types';
import { getColumns, isSelectionDisabled } from './columns';
import { BulkRequestTypeEnum } from '../BulkRequestTypeEnum';
import { getNo } from '../../../core/utils/entityNumberingHandle';

import './StockBulkTable.scss';

const defaultFilter = {
  linkedEntityNumber: '',
  linkedEntityStatus: '',
  productNumber: '',
};

interface StockBulkTableProps extends BulkTableProps {
  bulkRequestType: BulkRequestTypeEnum | null;
  onEditLocation?: (row: PartRequestBulkItemDto) => void;
  onEditOwner?: (row: PartRequestBulkItemDto) => void;
}

export const StockBulkTable = ({
  onFilter = noop,
  onSelectionChange = noop,
  onEditLocation = noop,
  onEditOwner = noop,
  filter,
  rows,
  bulkCount,
  selectedCount,
  totalCount,
  bulkRequestType,
}: StockBulkTableProps) => {
  const [innerFilter, setInnerFilter] = useState<TableFilter>(() => defaultFilter);

  useEffect(() => {
    setInnerFilter(getTableFilter(filter));
  }, [filter]);

  const handleChangeFilter = useCallback((value: string, key: string) => {
    if (!isFilterKey(key)) {
      return;
    }

    setInnerFilter((current) => ({ ...current, [key]: value }));

    if (!value) {
      onFilter(key, value);
    }
  }, [onFilter]);

  const handleSubmitFilter = useCallback((key: string) => {
    if (!isFilterKey(key)) {
      return;
    }

    const valueToSubmit = innerFilter[key] ?? '';
    onFilter(key, valueToSubmit);
  }, [innerFilter, onFilter]);

  const lastSelectIntent = useRef<boolean | null>(null);

  const handleBulkSelection = useCallback((selected: boolean) => {
    // Resolve bulk selection base on last user intent
    // (cover scenario with disabled lines, because it could not be selected, so selec all action goes to select partial)
    const nextBulkSelection = isBoolean(lastSelectIntent.current) && lastSelectIntent.current === selected
      ? !selected : selected;
    lastSelectIntent.current = nextBulkSelection;
    onSelectionChange(nextBulkSelection, null);
  }, [onSelectionChange]);

  const handleItemSelect = useCallback((selected: boolean, lineId: number) => {
    lastSelectIntent.current = null;
    onSelectionChange(selected, lineId);
  }, [onSelectionChange]);

  const isEmpty = !totalCount;
  const shallDisplaySelectAll = isDefaultFilter(filter);
  const columns = useMemo(() => getColumns(bulkRequestType), [bulkRequestType]);

  return (
    <TableNew scrollOffset={[20, 10]} className="stockBulkTable">
      <Thead>
        <Tr>
          <ThNew checkbox>
            {shallDisplaySelectAll && (
              <Checkbox
                checked={selectedCount === bulkCount}
                indeterminate={!!selectedCount && selectedCount < bulkCount}
                onChange={handleBulkSelection}
                disabled={isEmpty}
                table
              />
            )}
          </ThNew>

          {map(columns, (column) => (
            <ThWithControls
              key={column.dataIndex}
              col={column}
              totalElements={totalCount}
              className={`stockBulkTable--${column.dataIndex}`}
              onChangeSearchHandle={handleChangeFilter}
              searchPressHandle={handleSubmitFilter}
              searchVl={innerFilter}
              tableFilters={filter}
            />
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {map(rows, (row, index) => (
          <StockBulkTableRow
            key={row.id}
            row={row}
            columns={columns}
            onEditLocation={onEditLocation}
            onEditOwner={onEditOwner}
            ordinalNumber={getNo(filter.page, filter.pageSize, index)}
          >
            <TdNew>
              <Checkbox
                checked={row.isSelected ?? false}
                onChange={(isSelected) => {
                  handleItemSelect(isSelected, row.lineId);
                }}
                disabled={isSelectionDisabled(bulkRequestType, row)}
                table
              />
            </TdNew>
          </StockBulkTableRow>
        ))}
        {isEmpty && <EmptyTableSection text="No records" colSpan={size(columns) + 1} />}
      </Tbody>
    </TableNew>
  );
};
